import { useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useMerchant } from "@auth";
import { activityOpens } from "@utils";
import { fetchMerchantActivities } from "@api/activitiesController";
import { channels } from "../../../realtime/channels";
import { useCoalescedInvalidation } from "../../../realtime/useCoalescedInvalidation";
import { useActivityFeedEventNames } from "../../../realtime/useActivityFeedEventNames";

// Rows fetched per page; the panel pulls the next page as its list scrolls near the end.
const PAGE_SIZE = 20;

// The general activity feed, scoped by the BFF to the user's role: a User-scoped role sees only
// their own work, a Branch-scoped role every user in their branch(es), a Merchant-scoped role
// everyone. Activities carry no branch, so non-merchant tiers are scoped by actor — impersonal rows
// (a payment received, a system credit change) only reach merchant-tier users. When rows can be
// someone else's the BFF names that user on the row (actorName) and the meta line ends with it. The
// rows are notes, payment requests/reminders, payments received, document sends, credit and customer
// changes, chat-created tasks, and the payment-plan / promise-to-pay actions.

// Bold-line title for the fixed-label events (document sends pluralise separately; the action
// events resolve by kind — see ACTION_KINDS).
const FIXED_TITLES = {
  NoteCreated: "Note added",
  PaymentRequestSent: "Payment request sent",
  PaymentReceived: "Payment received",
  PaymentRequestPaid: "Payment received",
  PaymentRequestReminderSent: "Reminder sent",
  CreditLimitChanged: "Credit limit changed",
  CreditCodeChanged: "Account status changed",
  InvoiceInQuery: "Invoice in query",
  CustomerCreated: "Customer created",
  CustomerDeleted: "Customer removed",
  TaskCreated: "Task created",
  EmailSent: "Email sent",
};

// Icon key per event (keys ActivityPanel's ICONS). ActionStarted/ActionCancelled are resolved
// by kind instead, so plan/promise pick up the calendar glyph.
const EVENT_ICONS = {
  PaymentReceived: "payment",
  PaymentRequestPaid: "payment",
  PaymentRequestSent: "request",
  PaymentRequestReminderSent: "reminder",
  DocumentSent: "document",
  EmailSent: "email",
  CreditLimitChanged: "risk",
  CreditCodeChanged: "risk",
  InvoiceInQuery: "query",
  CustomerCreated: "customer",
  CustomerDeleted: "customer",
  NoteCreated: "note",
  TaskCreated: "task",
};

// Payment plan and promise to pay both arrive as ActionStarted, so classify by the action name
// the server puts in the title; each gets its own wording + icon (matching the calendar). The
// BFF only sends these two action kinds to this feed. A merchant-scheduled payment request shares
// the promise-to-pay title shape, so it's told apart by the flat scheduleKind field (see resolveVisuals)
// rather than a regex here — this fallback stays for rows from before that field existed.
const ACTION_KINDS = [
  { match: /^payment plan/i, icon: "plan", title: "Payment plan created" },
  { match: /^promise to pay/i, icon: "promise", title: "Promise to pay scheduled" },
];

// A payment request sent as part of a plan / promise carries the plan's glyph (with a small send
// badge) instead of the plain send icon, so a scheduled send reads as such. Keyed on the action
// type the BFF resolves onto the row.
const ACTION_TYPE_ICONS = {
  PaymentPlan: "plan",
  PromiseToPay: "promise",
};

// Relative time: seconds → minutes → hours → days ago.
const timeAgo = (iso) => {
  if (!iso) return "";
  const secs = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  const [value, unit] =
    secs < 60 ? [secs, "second"]
    : secs < 3600 ? [Math.floor(secs / 60), "minute"]
    : secs < 86400 ? [Math.floor(secs / 3600), "hour"]
    : [Math.floor(secs / 86400), "day"];
  return `${value} ${unit}${value === 1 ? "" : "s"} ago`;
};

// Turn a PascalCase event code into words as a last resort ("PaymentReceived" →
// "Payment received"). Only hit when the server sends no human displayText/label.
const humanize = (code = "") =>
  code
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase());

// Resolves a REST activity into its { type: icon key, title: bold line }. Action events are
// classified by kind (own wording + calendar glyph); document sends pluralise on the count;
// everything else maps straight from FIXED_TITLES / EVENT_ICONS.
const resolveVisuals = (a) => {
  const text = a.title ?? a.displayText ?? "";
  const scheduleKind = a.scheduleKind;

  // Payment plan / promise to pay starting (the BFF only sends these two action kinds). A
  // merchant-scheduled send shares the promise shape but reads its own icon key + title.
  if (a.eventName === "ActionStarted") {
    if (scheduleKind === "schedule") {
      return { type: "schedule", title: "Scheduled payment request scheduled" };
    }
    const kind = ACTION_KINDS.find((k) => k.match.test(text));
    if (kind) return { type: kind.icon, title: kind.title };
    return { type: "action", title: text.replace(/\s+(started|created|scheduled)$/i, "") || "Action" };
  }

  if (a.eventName === "DocumentSent") {
    return { type: "document", title: "Documents sent" };
  }

  // A payment request sent as part of a plan / promise takes the plan glyph plus a send badge.
  if (a.eventName === "PaymentRequestSent") {
    if (scheduleKind === "schedule") {
      return { type: "schedule", title: FIXED_TITLES.PaymentRequestSent, badge: "request" };
    }
    if (ACTION_TYPE_ICONS[a.originatingActionType]) {
      return {
        type: ACTION_TYPE_ICONS[a.originatingActionType],
        title: FIXED_TITLES.PaymentRequestSent,
        badge: "request",
      };
    }
  }

  return {
    type: EVENT_ICONS[a.eventName] ?? "note",
    title: FIXED_TITLES[a.eventName] ?? humanize(a.eventName) ?? "Activity",
  };
};

// Maps a REST activity DTO into the panel's { id, type, title, meta } shape. The sub-line carries
// just the customer and when; the user who did it leads it as an initials avatar (kept off the text).
export const toItem = (a) => {
  const { type, title: baseTitle, badge } = resolveVisuals(a);
  // A reminder on a payment plan / promise-to-pay instalment reads which one it's for and
  // becomes clickable (via actionId below) through to the plan modal, matching "Payment
  // received" rows. A reminder on a manual (non-plan) request has no instalment fields and
  // keeps the plain title.
  const title =
    a.eventName === "PaymentRequestReminderSent" && a.instalmentNumber != null && a.instalmentCount != null
      ? `${baseTitle} · Instalment ${a.instalmentNumber} of ${a.instalmentCount}`
      : baseTitle;
  const meta = [a.customerLabel, timeAgo(a.occurredAt)].filter(Boolean).join(" · ");
  // Payment rows carry the action link in originatingActionId; an ActionStarted row (payment plan
  // created / promise to pay scheduled) carries it as its primary entity — either lets the row open
  // the plan/promise detail modal.
  const actionId =
    a.originatingActionId ??
    (a.eventName === "ActionStarted" ? a.primaryEntityId : null) ??
    null;
  // What clicking the row opens — see utils/activityDetail.js (shared with the Smart Note feed).
  const opens = activityOpens(a);
  return { id: a.id, type, title, meta, amount: a.amount ?? null, actorName: a.actorName ?? null, badge, actionId, opens, activity: a };
};

/**
 * Role-scoped activity feed for the Today's-tasks panel. All-time activity, newest first, is
 * fetched from the BFF (which applies the user's role scope) a page at a time — the first page up front, the rest
 * as the panel scrolls towards its end (fetchNextPage / hasNextPage, react-query's infinite-query
 * shape). The realtime channel delivers lightweight "activity happened" hints; a burst of them is
 * folded into one refetch of the loaded pages (useCoalescedInvalidation), so new rows render with
 * full data without a bulk load turning every open session into a request storm.
 */
export const useActivityFeed = () => {
  const { merchant } = useMerchant();
  const queryKey = ["merchantActivities", merchant?.id];

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam, signal }) =>
      fetchMerchantActivities({
        // No date bound: the feed reaches back as far as the cursor goes.
        limit: PAGE_SIZE,
        cursor: pageParam,
        signal,
      }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage?.nextCursor ?? undefined,
    enabled: !!merchant?.id,
    staleTime: 30_000,
    retry: false,
  });

  const items = useMemo(() => {
    const rows = data?.pages.flatMap((page) => page?.items ?? []) ?? [];
    if (import.meta.env.DEV) {
      console.debug(
        "[Activity] items:",
        rows.map((a) => ({ event: a.eventName, customer: a.customerLabel, customerId: a.customerId })),
      );
    }
    return rows.map(toItem);
  }, [data]);

  const channelPath = merchant?.id ? channels.merchantActivity(merchant.id) : null;
  if (import.meta.env.DEV) {
    console.debug("[Activity] merchant:", merchant?.id ?? "(none)", "→ subscribing to:", channelPath);
  }
  const eventNames = useActivityFeedEventNames("general");
  useCoalescedInvalidation(channelPath, queryKey, { eventNames });

  return { items, fetchNextPage, hasNextPage: !!hasNextPage, isFetchingNextPage };
};
